import React, { useRef } from "react";
import { X } from "lucide-react";
import classNames from "classnames";
import { useNavigate } from "react-router";
import { usePathname } from "@/hooks/use-pathname";
import { useClickOutside } from "@/hooks/use-click-outside";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  navs: NavItem[];
  hamburgerRef: React.RefObject<HTMLButtonElement | null>;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  navs,
  hamburgerRef,
}) => {
  const navigate = useNavigate();
  const { pathname } = usePathname();
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(mobileMenuRef, onClose, hamburgerRef);

  const isActiveRoute = (navPath: string) => {
    if (navPath === "/") {
      return pathname === "/";
    }
    return pathname === navPath || pathname.startsWith(navPath + "/");
  };

  const getActiveNavLabel = () => {
    const activeNav = navs.find((nav) => isActiveRoute(nav.path));
    return activeNav ? activeNav.label : "Navigation";
  };

  const handleMobileNavClick = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {isOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-98" />
      )}
      <div
        ref={mobileMenuRef}
        className={classNames(
          "lg:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-black/90 backdrop-blur-xl border-l-2 border-primary-1 transform transition-transform duration-300 ease-in-out z-99",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 ">
            <h2 className="text-lg font-bold text-white">
              {getActiveNavLabel()}
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-white/10 transition-colors duration-200"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
          <nav className="flex-1 px-6 py-6">
            <ul className="space-y-3">
              {navs.map((nav: NavItem, index: number) => (
                <li key={nav.path + index}>
                  <button
                    onClick={() => handleMobileNavClick(nav.path)}
                    className={classNames(
                      "w-full text-left px-4 py-4 rounded-lg font-medium text-base transition-all duration-200 hover:bg-white/10",
                      isActiveRoute(nav.path)
                        ? "text-primary-1 bg-primary-1/10"
                        : "text-white",
                    )}
                  >
                    {nav.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
